import React from 'react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    Legend,
    LineChart,
    Line,
} from 'recharts';
import { cn, getChannelColor } from '../../lib/utils';

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="custom-tooltip bg-surface-card border border-line-default rounded-lg p-3 shadow-xl">
                <p className="text-xs text-content-muted mb-2">{label}</p>
                {payload.map((entry, index) => (
                    <div key={index} className="flex items-center gap-2">
                        <div 
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: entry.color }}
                        />
                        <span className="text-sm text-white font-medium">
                            {entry.name}: {entry.value.toLocaleString()}
                        </span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

export const TrendChart = ({ data, dataKey, title, color = '#FF3B30', className }) => {
    return (
        <div className={cn('card-base p-6', className)} data-testid={`trend-chart-${dataKey}`}>
            {title && (
                <h3 className="text-lg font-heading font-bold text-white mb-4">
                    {title}
                </h3>
            )}
            <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={data}>
                    <defs>
                        <linearGradient id={`gradient-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                    <XAxis 
                        dataKey="name" 
                        stroke="#52525B" 
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                    />
                    <YAxis 
                        stroke="#52525B" 
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                        type="monotone"
                        dataKey={dataKey}
                        stroke={color}
                        strokeWidth={2}
                        fill={`url(#gradient-${dataKey})`}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

export const BarChartComponent = ({ data, dataKeys, title, className }) => {
    const colors = ['#FF3B30', '#0077B5', '#34A853', '#F59E0B'];

    return (
        <div className={cn('card-base p-6', className)} data-testid="bar-chart">
            {title && (
                <h3 className="text-lg font-heading font-bold text-white mb-4">
                    {title}
                </h3>
            )}
            <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                    <XAxis 
                        dataKey="name" 
                        stroke="#52525B" 
                        fontSize={12}
                        tickLine={false}
                    />
                    <YAxis 
                        stroke="#52525B" 
                        fontSize={12}
                        tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {dataKeys.map((key, index) => (
                        <Bar
                            key={key}
                            dataKey={key}
                            fill={colors[index % colors.length]}
                            radius={[4, 4, 0, 0]}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export const PieChartComponent = ({ data, title, className }) => {
    const COLORS = ['#FF3B30', '#0077B5', '#34A853', '#F59E0B', '#8B5CF6'];

    return (
        <div className={cn('card-base p-6', className)} data-testid="pie-chart">
            {title && (
                <h3 className="text-lg font-heading font-bold text-white mb-4">
                    {title}
                </h3>
            )}
            <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                    >
                        {data.map((entry, index) => (
                            <Cell 
                                key={`cell-${index}`} 
                                fill={COLORS[index % COLORS.length]} 
                            />
                        ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                        formatter={(value) => (
                            <span className="text-xs text-content-secondary">{value}</span>
                        )}
                    />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};

export const MultiLineChart = ({ data, lines, title, className }) => {
    return (
        <div className={cn('card-base p-6', className)} data-testid="multi-line-chart">
            {title && (
                <h3 className="text-lg font-heading font-bold text-white mb-4">
                    {title}
                </h3>
            )}
            <ResponsiveContainer width="100%" height={250}>
                <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis 
                        dataKey="name" 
                        stroke="#52525B" 
                        fontSize={12}
                        tickLine={false}
                    />
                    <YAxis 
                        stroke="#52525B" 
                        fontSize={12}
                        tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                        wrapperStyle={{ paddingTop: '10px' }}
                        formatter={(value) => (
                            <span className="text-xs text-content-secondary">{value}</span>
                        )}
                    />
                    {lines.map((line, index) => (
                        <Line
                            key={line.dataKey}
                            type="monotone"
                            dataKey={line.dataKey}
                            name={line.name}
                            stroke={line.color || getChannelColor(line.dataKey)}
                            strokeWidth={2}
                            dot={false}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default TrendChart;
